import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

// =============================================================================
// Types — content est désormais un simple Markdown string (approche style Quo).
// Pas de JSON Schema imposé : le LLM choisit lui-même la meilleure structure
// (tableau pour des tarifs, liste pour des services, etc.).
// =============================================================================

export interface KnowledgePage {
  id: number
  agent: number
  source_url: string
  title: string
  enabled: boolean
  /** Markdown libre. Affiché via react-markdown + remark-gfm en mode aperçu. */
  content: string
  raw_scraped_text: string
  scraped_at: string | null
  created_at: string
  updated_at: string
}

/**
 * Réponse du preview-import : pas encore sauvegardé en DB, juste un aperçu.
 * Le frontend passe ce payload à la mutation `useCreateKnowledgePage` après
 * que l'utilisateur a validé/édité.
 */
export interface KnowledgePagePreview {
  source_url: string
  title: string
  content: string
  raw_scraped_text: string
}

export interface KnowledgePageCreateInput {
  title: string
  source_url?: string
  content: string
  raw_scraped_text?: string
  enabled?: boolean
}

export interface KnowledgePageUpdateInput {
  title?: string
  enabled?: boolean
  content?: string
  source_url?: string
}

export interface KnowledgePageMigrationResult {
  created: KnowledgePage[]
  skipped: Array<{ reason: string; url_or_title: string }>
  errors: Array<{ url: string; error: string }>
}

// =============================================================================
// URL builders
// =============================================================================

const base = (companyId: number, agentId: number) =>
  `/api/companies/${companyId}/ai-agents/${agentId}/knowledge-pages`

// =============================================================================
// Hooks
// =============================================================================

/** GET liste des pages de connaissance. */
export function useKnowledgePages(
  companyId: number | undefined,
  agentId: number | undefined,
) {
  return useQuery({
    queryKey: ['knowledge-pages', companyId, agentId],
    queryFn: async () => {
      const { data } = await api.get<KnowledgePage[]>(
        `${base(companyId!, agentId!)}/`,
      )
      return data
    },
    enabled: !!companyId && !!agentId,
    staleTime: 30_000,
  })
}

/**
 * POST preview-import — scrape une URL + conversion en Markdown par GPT-4o-mini.
 * Ne sauvegarde rien : retourne juste l'aperçu pour édition par l'utilisateur.
 *
 * Le pipeline (Jina Reader → GPT-4o-mini) peut prendre 10-30 secondes selon
 * la taille de la page. On surcharge le timeout global axios (30s) à 120s
 * pour donner du mou aux pages volumineuses (sites WordPress riches, etc.).
 */
export function usePreviewImportKnowledgePage(
  companyId: number | undefined,
  agentId: number | undefined,
) {
  return useMutation({
    mutationFn: async (url: string) => {
      const { data } = await api.post<KnowledgePagePreview>(
        `${base(companyId!, agentId!)}/preview-import/`,
        { url },
        { timeout: 120_000 },
      )
      return data
    },
  })
}

/** POST création d'une page (manuelle ou validée après preview). */
export function useCreateKnowledgePage(
  companyId: number | undefined,
  agentId: number | undefined,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: KnowledgePageCreateInput) => {
      const { data } = await api.post<KnowledgePage>(
        `${base(companyId!, agentId!)}/`,
        input,
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge-pages', companyId, agentId] })
    },
  })
}

/** PATCH update partiel (title, enabled, content). */
export function useUpdateKnowledgePage(
  companyId: number | undefined,
  agentId: number | undefined,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      pageId,
      input,
    }: {
      pageId: number
      input: KnowledgePageUpdateInput
    }) => {
      const { data } = await api.patch<KnowledgePage>(
        `${base(companyId!, agentId!)}/${pageId}/`,
        input,
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge-pages', companyId, agentId] })
    },
  })
}

/** DELETE suppression définitive. */
export function useDeleteKnowledgePage(
  companyId: number | undefined,
  agentId: number | undefined,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (pageId: number) => {
      await api.delete(`${base(companyId!, agentId!)}/${pageId}/`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge-pages', companyId, agentId] })
    },
  })
}

/** POST rescrape — relance scraping+conversion sur l'URL d'origine (timeout long). */
export function useRescrapeKnowledgePage(
  companyId: number | undefined,
  agentId: number | undefined,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (pageId: number) => {
      const { data } = await api.post<KnowledgePage>(
        `${base(companyId!, agentId!)}/${pageId}/rescrape/`,
        undefined,
        { timeout: 120_000 },
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge-pages', companyId, agentId] })
    },
  })
}

/**
 * Réponse de l'endpoint clean-content.
 * Stateless : rien n'a été sauvegardé. Le SPA injecte le contenu nettoyé
 * dans l'éditeur ; l'utilisateur sauvegarde via POST (mode preview) ou
 * PATCH (mode edit) selon le cas.
 */
export interface KnowledgePageCleanResult {
  cleaned_content: string
  original_chars: number
  cleaned_chars: number
  compression_ratio: number
}

/**
 * POST clean-content — compacte un markdown pour un agent vocal.
 * Stateless : prend le contenu en body, retourne la version nettoyée.
 * Marche pour les deux modes : preview (juste après scrape) et edit
 * (page déjà sauvegardée).
 */
export function useCleanKnowledgeContentWithAI(
  companyId: number | undefined,
  agentId: number | undefined,
) {
  return useMutation({
    mutationFn: async (content: string) => {
      const { data } = await api.post<KnowledgePageCleanResult>(
        `${base(companyId!, agentId!)}/clean-content/`,
        { content },
        { timeout: 120_000 },
      )
      return data
    },
  })
}

/**
 * POST migration des champs legacy vers knowledge_pages.
 * Peut scrapper plusieurs URLs en série — timeout 5 min pour les agents avec
 * beaucoup de knowledge_web_urls.
 */
export function useMigrateLegacyKnowledge(
  companyId: number | undefined,
  agentId: number | undefined,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<KnowledgePageMigrationResult>(
        `${base(companyId!, agentId!)}/migrate-legacy/`,
        undefined,
        { timeout: 300_000 },
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge-pages', companyId, agentId] })
    },
  })
}

// =============================================================================
// Import of a whole site: find the useful pages, then import up to 5 of them in
// the background. Each page is read, cleaned by AI, saved; the screen polls the
// state page by page.
// =============================================================================

export const MAX_SITE_PAGES = 5

export interface SitePage {
  url: string
  titre: string
  /** Pre-checked: among the most useful pages. */
  proposee: boolean
}

export interface SitePagesResult {
  site: string
  pages: SitePage[]
}

/** 'prete': read and cleaned, waiting for the agent the creation assistant is about to create. */
export type SiteImportPageState = 'attente' | 'lecture' | 'nettoyage' | 'prete' | 'enregistree' | 'deja' | 'echec'

export interface SiteImportPage {
  url: string
  titre: string
  etat: SiteImportPageState
  /** Why a page failed: injoignable, vide, adresse_refusee, lecture. */
  motif: string
  page_id: number | null
  caracteres: number
}

export interface SiteImport {
  /** 'creation' and 'echec': the creation assistant, once the pages are read. */
  statut: 'en_cours' | 'mise_a_jour' | 'creation' | 'termine' | 'echec'
  debut: string
  fin?: string
  pages: SiteImportPage[]
  /** The agent the creation assistant created. */
  agent_id?: number | null
}

/** POST site/pages/ — the useful pages of a site (reads its homepage and sitemap). */
export function useFindSitePages(companyId: number | undefined, agentId: number | undefined) {
  return useMutation({
    mutationFn: async (url: string) => {
      const { data } = await api.post<SitePagesResult>(
        `${base(companyId!, agentId!)}/site/pages/`,
        { url },
        { timeout: 120_000 },
      )
      return data
    },
  })
}

/** POST site/import/ — starts the background import, returns the id to poll. */
export function useStartSiteImport(companyId: number | undefined, agentId: number | undefined) {
  return useMutation({
    mutationFn: async (pages: Array<{ url: string; titre: string }>) => {
      const { data } = await api.post<{ import_id: string }>(`${base(companyId!, agentId!)}/site/import/`, { pages })
      return data.import_id
    },
  })
}

/** GET site/import/<id>/ — polled every 2 s until the import is over. */
export function useSiteImport(
  companyId: number | undefined,
  agentId: number | undefined,
  importId: string | null,
) {
  return useQuery({
    queryKey: ['knowledge-site-import', companyId, agentId, importId],
    queryFn: async () => {
      const { data } = await api.get<SiteImport>(`${base(companyId!, agentId!)}/site/import/${importId}/`)
      return data
    },
    enabled: !!companyId && !!agentId && !!importId,
    refetchInterval: (query) => (query.state.data?.statut === 'termine' || query.state.error ? false : 2_000),
    retry: false,
  })
}
